import React, { useMemo } from "react";
import { Card, CardBody, Button } from "reactstrap";
import { AlertCircle } from "react-feather";
import { useHistory } from "react-router-dom";
import { connect } from "react-redux";
import { getTaskProjectId } from "../../../redux/actions/task/task.action";

const MissingTimesheet = ({ tasks = [], leaves = [], empTask, getTaskProjectId }) => {
  const history = useHistory(); // ✅ MUST be here (top level)

  const { missingDays, monthLabel } = useMemo(() => {
    const today = new Date();

    // 🔹 Find the first day of the current month
    const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);

    // 🔹 Build Mon–Fri dates for the entire month up to today
    const monthDays = [];
    let d = new Date(firstDay);
    while (d <= today) {
      const dayOfWeek = d.getDay();
      // Only add Mon(1) to Fri(5)
      if (dayOfWeek !== 0 && dayOfWeek !== 6) {
        monthDays.push(new Date(d));
      }
      d.setDate(d.getDate() + 1);
    }

    // 🔹 Group tasks/leaves by date
    const dateMap = {};
    tasks.forEach(t => {
      const dateVal = t.workDate || t.WorkDate || t.statusDate || t.StatusDate;
      if (!dateVal) return;

      const dateKey = new Date(dateVal).toDateString();
      if (!dateMap[dateKey]) {
        dateMap[dateKey] = {
          hoursLogged: 0,
          isFullDayLeave: false,
          isHalfDayLeave: false
        };
      }

      const isL = t.leaveStatus === true || t.leaveStatus === 1 || t.LeaveStatus === true || t.LeaveStatus === 1;
      const lType = t.leaveType || t.LeaveType;

      if (isL) {
        if (lType === 2) {
          dateMap[dateKey].isFullDayLeave = true;
        } else if (lType === 1) {
          dateMap[dateKey].isHalfDayLeave = true;
        }
      } else {
        dateMap[dateKey].hoursLogged += Number(t.hoursSpent || t.HoursSpent || 0);
      }
    });

    // Group leaves (leaverequests from tasksGraph) by date to include approved/pending leaves
    leaves.forEach(l => {
      const statusId = l.leaveStatusId ?? l.LeaveStatusId ?? l.dutyStatusId ?? l.DutyStatusId;
      if (statusId != null && statusId !== 1 && statusId !== 2) return;
      if (l.leaveStatus !== 1 && l.LeaveStatus !== 1) return;

      const start = new Date(l.fromDate || l.FromDate);
      const end = new Date(l.toDate || l.ToDate);
      if (isNaN(start.getTime()) || isNaN(end.getTime())) return;

      start.setHours(0, 0, 0, 0);
      end.setHours(0, 0, 0, 0);

      let temp = new Date(start);
      while (temp <= end) {
        const dateKey = temp.toDateString();
        if (!dateMap[dateKey]) {
          dateMap[dateKey] = {
            hoursLogged: 0,
            isFullDayLeave: false,
            isHalfDayLeave: false
          };
        }

        const noOfDays = l.noOfDays || l.NoOfDays || 1;
        if (noOfDays === 0.5) {
          dateMap[dateKey].isHalfDayLeave = true;
        } else {
          dateMap[dateKey].isFullDayLeave = true;
        }

        temp.setDate(temp.getDate() + 1);
      }
    });

    // 🔹 Missing days based on the conditions
    const missing = monthDays
      .filter(d => {
        const dateKey = d.toDateString();
        const entry = dateMap[dateKey];

        // If no entry exists at all, it's missing (0 hours, no leave)
        if (!entry) {
          return true;
        }

        // 1. The person is leave on full day, no need to show in missing timesheet
        if (entry.isFullDayLeave) {
          return false;
        }

        // 2. If employee is on half day leave and the duty entry is less than 4 hours then show missing timesheet
        if (entry.isHalfDayLeave) {
          return entry.hoursLogged < 4;
        }

        // 3. If employee didn't apply any leave and he have less than 8 hr duty. Then we can show missing timesheet entry
        return entry.hoursLogged < 8;
      })
      .map(d => ({
        day: d.toLocaleDateString("en-US", { weekday: "short" }),
        date: d.toLocaleDateString("en-US", {
          weekday: "long",
          month: "short",
          day: "numeric"
        })
      }))
      .reverse(); // Show recent missing dates first

    // 🔹 Month label
    const monthLabel = today.toLocaleDateString("en-US", {
      month: "long",
      year: "numeric"
    });

    return { missingDays: missing, monthLabel };
  }, [tasks, leaves]);

  const handleClick = () => {
    if (empTask && empTask.taskProjectInfo && empTask.taskProjectInfo.projectId) {
      getTaskProjectId(
        empTask.taskProjectInfo.projectId,
        empTask.taskProjectInfo.projectName,
        empTask.taskProjectInfo.status
      );
    }
    history.push("/taskManagment");
  };
  return (
    <Card className="h-100">
      <CardBody>
        {/* Header */}
        <div className="d-flex align-items-start gap-3 mb-3">

          {/* Icon */}
          <div className="icon-box bg-warning-light">
            <AlertCircle size={20} className="text-warning" />
          </div>

          {/* Title + Subtitle */}
          <div>
            <h5 className="mb-1 fw-semibold">
              Missing Timesheet Entries
            </h5>
            <p className="text-muted small mb-0">
              Month of {monthLabel}
            </p>
          </div>

        </div>

        {/* Missing list */}
        <div className="mt-3" style={{ maxHeight: "300px", overflowY: "auto", paddingRight: "5px" }}>
          {missingDays.length === 0 && (
            <div className="text-muted text-center py-3">
              🎉 All timesheets filled for this month
            </div>
          )}

          {missingDays.map((item, index) => (
            <div
              key={index}
              className="p-3 rounded mb-3 d-flex justify-content-between align-items-center"
              style={{ background: "#fff4e6" }}
            >
              <div>
                <strong>{item.day}</strong>
                <div className="text-muted small">
                  {item.date}
                </div>
              </div>
              <span className="badge bg-warning rounded-circle">
                &nbsp;
              </span>
            </div>
          ))}
        </div>

        <Button color="warning" className="w-100 mt-2" onClick={handleClick}>
          Fill Timesheet
        </Button>
      </CardBody>
    </Card>
  );
};

const mapStateToProps = (state) => ({
  empTask: state.taskReducer,
});

export default connect(mapStateToProps, { getTaskProjectId })(MissingTimesheet);
